<script setup lang="ts">
import { computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import SsnInput from '../../Shared/SSNInput.vue'

const { t } = useI18n()
const { umsokn, onLoaded } = useUmsokn()

const props = defineProps<{ disable?: boolean }>()

// ✅ Dynamic validation rules per entry
const rules = computed(() => ({
  umsokn: {
    currentDeptorInformation: Array.isArray(umsokn.value?.currentDeptorInformation)
      ? umsokn.value.currentDeptorInformation.map(() => ({
          name: { required },
          ssn: { required }
        }))
      : []
  }
}))

const v$ = useVuelidate(rules, { umsokn })

// ✅ Initialize list and reset validation
onLoaded(() => {
  if (
    !Array.isArray(umsokn.value?.currentDeptorInformation) ||
    umsokn.value.currentDeptorInformation.length === 0
  ) {
    umsokn.value.currentDeptorInformation = [{ name: '', ssn: '', phone: '', email: '' }]
  }
  nextTick(() => v$.value.$reset())
})

// ✅ Add/remove helpers
function addCoDeptor() {
  umsokn.value?.currentDeptorInformation.push({ name: '', ssn: '', phone: '', email: '' })
}

function removeCoDeptor(index: number) {
  umsokn.value?.currentDeptorInformation.splice(index, 1)
  if (umsokn.value?.currentDeptorInformation.length === 0) addCoDeptor()
}

// ✅ Expose validation
defineExpose({
  async validate() {
    if (Array.isArray(umsokn.value.currentDeptorInformation)) {
      umsokn.value.currentDeptorInformation.forEach((_: unknown, i: number) => {
        v$.value.umsokn.currentDeptorInformation[i].$touch()
      })
    }
    const isValid = await v$.value.$validate()
    return !v$.value.$invalid && isValid
  }
})
</script>

<template>
  <div v-if="umsokn">
    <v-col>
      <div class="forms__header">
        {{ t('lan_nafnabeyting_umsokn_step6_header1') }}
      </div>

      <transition-group name="list">
        <div
          v-for="(deptor, index) in umsokn.currentDeptorInformation"
          :key="'currentDeptorInfo_' + index"
          class="list-item"
        >
          <div v-if="index > 0" class="forms__divider__container">
            <div class="forms__divider"></div>
            <button
              v-if="!props.disable"
              type="button"
              class="forms__warning__list__wrapper"
              @click="removeCoDeptor(index)"
            >
              <img class="forms__warning__icon" src="@/assets/icons/Close.svg" alt="Remove" />
            </button>
          </div>

          <v-row>
            <!-- Name -->
            <v-col md="6" cols="12">
              <v-text-field
                v-model="deptor.name"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step6_nafn')"
                variant="outlined"
                density="comfortable"
                :error="v$.umsokn.currentDeptorInformation[index]?.name?.$error"
                :error-messages="v$.umsokn.currentDeptorInformation[index]?.name?.$dirty
                  ? [
                      v$.umsokn.currentDeptorInformation[index]?.name?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : ''
                    ].filter(Boolean)
                  : []"
              />
            </v-col>

            <!-- SSN -->
            <v-col md="6" cols="12">
              <SsnInput
                v-model="deptor.ssn"
                :readonly="props.disable"
                :label="t('lan_nafnabeyting_umsokn_step6_kennitala')"
                variant="outlined"
                :error="v$.umsokn.currentDeptorInformation[index]?.ssn?.$error"
                :error-messages="v$.umsokn.currentDeptorInformation[index]?.ssn?.$dirty
                  ? [
                      v$.umsokn.currentDeptorInformation[index]?.ssn?.required?.$invalid
                        ? t('umsokn_general_required_error')
                        : '',
                    ].filter(Boolean)
                  : []"
              />
            </v-col>
          </v-row>
        </div>
      </transition-group>

      <div class="m-t-40" v-if="!props.disable">
         <v-btn
          color="primary"
          variant="outlined"
          prepend-icon="mdi-plus"
          class="mt-4"
          @click="addCoDeptor"
        >
          {{ $t('lan_nafnabeyting_umsokn_step6_add') }}
        </v-btn>
      </div>
    </v-col>
  </div>
</template>

